<script lang="ts" setup>
import type { DetailsWheels } from '@autobid/ui/types/components/CarDetails'
import { useDetailsWheels } from '@autobid/ui/composables/car/useDetailsWheels'
import { computed } from 'vue'

interface Props {
  title: string
  wheels?: DetailsWheels
}

const props = defineProps<Props>()

const { tableWithWheelTitles } = useDetailsWheels(props.wheels)
const shouldBeRendered = computed(() => {
  const { list, images, tables } = props.wheels ?? {}

  return list?.length || images?.length || tables?.find(el => el.table.tableCells?.length)
})
</script>

<template>
  <div v-if="shouldBeRendered" class="col-span-12">
    <SectionsDetailsElementsSection>
      <template #heading>
        {{ title }}
      </template>

      <template #content>
        <div
          v-if="wheels?.list?.length"
          class="mb-2 grid grid-cols-2 overflow-hidden"
        >
          <div
            v-for="element in wheels.list"
            :key="String(element)"
            class="wheels-list-data col-span-2 flex border-b border-[#c1c1c1] px-1 py-4"
            :class="{ 'lg:col-span-1': !Array.isArray(element[1]) }"
          >
            <p class="min-w-[190px] max-w-[190px] font-bold">
              {{ element[0] }}:
            </p>
            <ul v-if="Array.isArray(element[1])" class="w-full">
              <li
                v-for="(values, index) in element[1]"
                :key="values[0]"
                class="relative block w-full"
                :class="{
                  'multivalue-list-element mt-3.5 pt-3.5': index > 0,
                }"
              >
                <span class="block lg:inline-block lg:w-[115px]">
                  {{ values[0] }}
                </span>
                <span>{{ values[1] }}</span>
              </li>
            </ul>
            <p v-else>
              {{ element[1] }}
            </p>
          </div>
        </div>

        <SectionsDetailsElementsImages
          v-if="wheels?.images?.length"
          class="my-3"
          :images="wheels.images"
        />

        <div
          v-if="wheels?.tables?.length"
          class="-mx-2 grid grid-cols-10 gap-2.5 md:-mx-4 md:overflow-x-auto"
        >
          <SectionsDetailsElementsSection
            v-for="(table, index) in tableWithWheelTitles"
            :key="table.title"
            content-class="!px-1 xl:px-2"
            variant="secondary"
            class="wheels-table-wrapper col-span-10"
            :class="{
              'lg:col-span-6': index === 0,
              'lg:col-span-2': index > 0,
            }"
          >
            <template #heading>
              <span class="block w-full text-center">
                {{ table.title }}
              </span>
            </template>

            <template #content>
              <SectionsDetailsElementsTable :table="table.table" />
            </template>
          </SectionsDetailsElementsSection>
        </div>
      </template>
    </SectionsDetailsElementsSection>
  </div>
</template>

<style lang="scss">
@media (max-width: 1158px) {
  .wheels-table {
    grid-column: span 10 / span 10;
  }
}

@media (min-width: 1024px) {
  // to not render title cells for another wheels tables - it should be rendered only for mobile devices
  .wheels-table-wrapper {
    &:not(:first-child) {
      th,
      td {
        &:first-child:not(.td-pill) {
          display: none;
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.wheels-list-data {
  &:nth-child(odd):last-child {
    grid-column: span 2 / span 2;
  }
}
.multivalue-list-element {
  --labelSize: 177px;

  &::before {
    content: '';
    position: absolute;
    left: calc(-1 * var(--labelSize));
    top: 0;
    width: calc(100% + var(--labelSize));
    border-top: 1px solid #c1c1c1;
  }
}
</style>
